import {
  Link,
  Outlet,
  createFileRoute,
  useMatchRoute,
} from "@tanstack/react-router";

import { Route as AffiliatesSettingsRoute } from "@/routes/_app/_auth/dashboard/_layout.settings.affiliates";
import { Route as BillingSettingsRoute } from "@/routes/_app/_auth/dashboard/_layout.settings.billing";
import { Route as CreatorInvitesSettingsRoute } from "@/routes/_app/_auth/dashboard/_layout.settings.creator-invites";
import { DEMO_IDS } from "@/mock/demo";
import { Route as InvitesSettingsRoute } from "@/routes/_app/_auth/dashboard/_layout.settings.invites";
import { Route as SettingsRoute } from "@/routes/_app/_auth/dashboard/_layout.settings.index";
import { Route as TeamSettingsRoute } from "@/routes/_app/_auth/dashboard/_layout.settings.team";
import { api } from "~/convex/_generated/api";
import { buttonVariants } from "@/components/ui/button-util";
import { cn } from "@/utils/misc";
import { convexQuery } from "@convex-dev/react-query";
import { useAccount } from "@/state/useAccount";
import { useQuery } from "@tanstack/react-query";

export const Route = createFileRoute("/_app/_auth/dashboard/_layout/settings")({
  component: DashboardSettingsLayout,
});

export default function DashboardSettingsLayout() {
  const matchRoute = useMatchRoute();
  const { appId, demoModeEnabled } = useAccount();

  // Check if we're in demo mode
  const isDemo =
    demoModeEnabled &&
    (appId as unknown as string) === (DEMO_IDS.appId as unknown as string);

  const isSettingsPath = matchRoute({ to: SettingsRoute.fullPath });
  const isTeamPath = matchRoute({ to: TeamSettingsRoute.fullPath });
  const isInvitesPath = matchRoute({ to: InvitesSettingsRoute.fullPath });
  const isCreatorInvitesPath = matchRoute({
    to: CreatorInvitesSettingsRoute.fullPath,
  });
  const isBillingPath = matchRoute({ to: BillingSettingsRoute.fullPath });
  const isAffiliatesPath = matchRoute({ to: AffiliatesSettingsRoute.fullPath });
  // Only fetch invites if we have an appId
  const { data: pendingInvites } = useQuery(
    convexQuery(api.invites.getAppFounderInvitesForUser, {}),
  );

  const { data: creatorInvites } = useQuery(
    convexQuery(api.invites.getCreatorInvites, { appId }),
  );

  // If in demo mode, use empty array for creator invites
  const effectiveCreatorInvites = isDemo ? [] : creatorInvites;

  const hasPendingInvites = pendingInvites?.some(
    (invite) => invite.status === "pending",
  );

  const hasPendingCreatorInvites = effectiveCreatorInvites?.some(
    (invite) => invite.status === "pending",
  );

  return (
    <div className="flex h-full w-full px-6 py-8">
      <div className="mx-auto flex h-full w-full max-w-screen-xl gap-12">
        <div className="hidden w-full max-w-64 flex-col gap-0.5 lg:flex">
          <Link
            to={SettingsRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: "ghost" })} ${isSettingsPath && "bg-primary/5"}`,
              "justify-start rounded-md",
            )}
          >
            <span
              className={cn(
                `text-sm text-primary/80 ${isSettingsPath && "font-medium text-primary"}`,
              )}
            >
              General
            </span>
          </Link>

          {appId && (
            <Link
              to={TeamSettingsRoute.fullPath}
              className={cn(
                `${buttonVariants({ variant: "ghost" })} ${isTeamPath && "bg-primary/5"}`,
                "justify-start rounded-md",
              )}
            >
              <span
                className={cn(
                  `text-sm text-primary/80 ${isTeamPath && "font-medium text-primary"}`,
                )}
              >
                Team
              </span>
            </Link>
          )}
          <Link
            to={BillingSettingsRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: "ghost" })} ${isBillingPath && "bg-primary/5"}`,
              "justify-start rounded-md",
            )}
          >
            <span
              className={cn(
                `text-sm text-primary/80 ${isBillingPath && "font-medium text-primary"}`,
              )}
            >
              Billing
            </span>
          </Link>

          {appId && hasPendingInvites && (
            <Link
              to={InvitesSettingsRoute.fullPath}
              className={cn(
                `${buttonVariants({ variant: "ghost" })} ${isInvitesPath && "bg-primary/5"}`,
                "justify-start rounded-md",
              )}
            >
              <span
                className={cn(
                  `text-sm text-primary/80 ${isInvitesPath && "font-medium text-primary"}`,
                )}
              >
                Team Invites
              </span>
            </Link>
          )}
          {appId && hasPendingCreatorInvites && (
            <Link
              to={CreatorInvitesSettingsRoute.fullPath}
              className={cn(
                `${buttonVariants({ variant: "ghost" })} ${isCreatorInvitesPath && "bg-primary/5"}`,
                "justify-start rounded-md",
              )}
            >
              <span
                className={cn(
                  `text-sm text-primary/80 ${isCreatorInvitesPath && "font-medium text-primary"}`,
                )}
              >
                Creator Invites
              </span>
            </Link>
          )}

          {appId && (
            <Link
              to={AffiliatesSettingsRoute.fullPath}
              className={cn(
                `${buttonVariants({ variant: "ghost" })} ${isAffiliatesPath && "bg-primary/5"}`,
                "justify-start rounded-md",
              )}
            >
              <span
                className={cn(
                  `text-sm text-primary/80 ${isAffiliatesPath && "font-medium text-primary"}`,
                )}
              >
                Affiliates
              </span>
            </Link>
          )}
        </div>

        <Outlet />
      </div>
    </div>
  );
}
